import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import useOrderCreation from "@/composables/api/useOrderCreation";
import useErrorHandling from "@/composables/api/useErrorHandling";
import useNFTOrderEndpoints from "@/composables/api/useNFTOrderEndpoints";

export default () => {
  const orderStore = useOrderFormStore();
  const fieldsRelatedToOrder = [
    "fiatAmount",
    "fiatType",
    "coinAmount",
    "coinType",
    "orderType",
    "walletAddress",
  ];
  const { handleErrorInSpaFlow } = useErrorHandling();
  const { defaultData, disableUiByApi } = useOrderCreation();
  const { getNFTAsset } = useNFTOrderEndpoints();
  /**
   * Checks the url for seatch query param and construct
   * the order data with key, value pair
   * @param {}
   * @returns {Object}
   * */

  const getUrlOrder = () => {
    const { searchParams } = new URL(window.location.href);

    const urlSearchData = {};

    if (!searchParams) {
      return;
    }

    for (const [key, value] of searchParams.entries()) {
      urlSearchData[key] = value;
    }

    // handle lockFiat logic for the calculator
    const searchParamKeys = Object.keys(urlSearchData);
    if (
      searchParamKeys.includes("coinAmount") &&
      !searchParamKeys.includes("fiatAmount")
    ) {
      urlSearchData.lockFiat = false;
    } else if (
      searchParamKeys.includes("fiatAmount") &&
      !searchParamKeys.includes("coinAmount")
    ) {
      urlSearchData.lockFiat = true;
    } else {
      urlSearchData.lockFiat = true;
    }

    if (searchParamKeys.includes("payment")) {
      delete urlSearchData.payment;
    }

    urlSearchData.coinType = searchParams.has("coinType") ? searchParams.get('coinType').toUpperCase() : orderStore.settings.defaultCoinType.toUpperCase();
    urlSearchData.fiatType = searchParams.has("fiatType") ? searchParams.get('fiatType').toUpperCase() : orderStore.settings.defaultFiatType.toUpperCase();
    urlSearchData.fiatAmount = searchParams.has("fiatAmount") ? Number(searchParams.get('fiatAmount')) : Number(orderStore.settings.defaultFiatAmount);
    return urlSearchData;
  };

  const isAnUrlOrder = (() => {
    const { searchParams } = new URL(window.location.href);
    const searchParamsSize = [...searchParams].length;
    const searchParamsIsNotAnApiOrder =
      !searchParams.has("id") && !searchParams.has("initId");
    const urlParamsRelatedToOrder = fieldsRelatedToOrder
      .map((field) => searchParams.has(field))
      .includes(true);

    return (
      urlParamsRelatedToOrder &&
      searchParamsIsNotAnApiOrder &&
      searchParamsSize > 0
    );
  })();

  const checkUrlForOrderTypeAndSetMode = async () => {
    const checkOrderTypeAndSetMode = (data) => {
      if (
        (!Object.getOwnPropertyDescriptor(data, 'orderType') &&
          !Object.getOwnPropertyDescriptor(data, 'orderMode')) ||
        (data.orderType && data.orderType.toUpperCase().indexOf("BUY") >= 0) ||
        (data.orderMode && data.orderMode.toUpperCase().indexOf("BUY") >= 0)
      ) {
        defaultData.orderMode = "BUY";
        orderStore.setActiveMode("BUY");
      } else {
        defaultData.orderMode = "SELL";
        orderStore.setActiveMode("SELL");
      }
    };

    if (!isAnUrlOrder) {
      return defaultData;
    }

    const urlOrder = getUrlOrder();

    defaultData.urlData = urlOrder;
    checkOrderTypeAndSetMode(urlOrder);
    defaultData.areDefaultsFromUrl = true;

    return defaultData;
  };

  const checkURLForNFTOrder = async () => {
    const urlOrder = getUrlOrder();
    const nftReferenceId = urlOrder.reference;

    if (!nftReferenceId) {
      handleErrorInSpaFlow("50399", "Fail to get NFT data for this order");
    }

    let nftAssetResponse = await getNFTAsset(nftReferenceId);
    // Put a default fiat type value if not given
    if (!nftAssetResponse.data.fiatType) {
      nftAssetResponse = {
        data: {
          ...nftAssetResponse.data.data.asset,
          ...nftAssetResponse.data.data.price,
          fiatType: urlOrder.fiatType || "USD",
        },
      };
    }

    const disableUIKeys = {
      fiatAmount: true,
      walletAddress: false,
    };

    if (urlOrder.walletAddress) {
      nftAssetResponse = {
        data: {
          ...nftAssetResponse.data,
          walletAddress: urlOrder.walletAddress,
        },
      };
      disableUIKeys.walletAddress = true;
    }

    return {
      orderMode: "NFT-BUY",
      lockFiat: false,
      areDefaultsFromUrl: true,
      urlData: {
        ...nftAssetResponse.data,
        ...urlOrder
      },
      disableUIKeys,
    };
  };

  const getUrlParams = () => {
    const { searchParams } = new URL(window.location.href);

    const hasParams = Array.from(searchParams.keys()).some((keys) =>
      fieldsRelatedToOrder.includes(keys)
    );

    return {
      hasParams: hasParams,
      walletAddress: searchParams.has("walletAddress") ? searchParams.get('walletAddress') : '',
      coinType: searchParams.has("coinType") ? searchParams.get('coinType').toUpperCase() : orderStore.settings.defaultCoinType.toUpperCase(),
      fiatType: searchParams.has("fiatType") ? searchParams.get('fiatType').toUpperCase() : orderStore.settings.defaultFiatType.toUpperCase(),
      fiatAmount: searchParams.has("fiatAmount") ? Number(searchParams.get('fiatAmount')) : Number(orderStore.settings.defaultFiatAmount), 
    }
  }

  return {
    isAnUrlOrder,
    checkUrlForOrderTypeAndSetMode,
    checkURLForNFTOrder,
    getUrlParams,
  };
};
